/// <amd-module name="Search/Wiki/Components/Pivot" />
import * as React from "react";
import { IVssSearchService } from "VSS/Platform/Search";
import * as Resources from "Search/Wiki/Resources";
import { ActionCreator } from "Search/Wiki/Flux/ActionCreator";
import { ActionsHub } from "Search/Wiki/Flux/ActionsHub";
import { applyNavigatedUrl, updateUrl } from "Search/Wiki/NavigationHandler";
import { Filter } from "SearchUI/Utilities/Filter";
import { getTFSPageData, ITfsPageData } from "Tfs/Platform/Page"
import { handlePivotNotification } from "Search/Wiki/Components/Notification";
import { handlePivotCommands } from "Search/Wiki/Components/PivotCommands";
import { ITabContext, IInputQuery } from "Search/Shared/Base/Types";
import { IVssHistoryService, INavigationStateChangedEvent } from "VSS/Platform/History";
import { isSearchOrgButtonEnabled } from "Search/Shared/Base/Sources/SearchOrgButtonAvailabilityHelper";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { OrgInfoDataProviderSource } from "Search/Shared/Base/Sources/OrgInfoDataProviderSource";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { ResultsContainer } from "Search/Wiki/Components/Results";
import { StoresHub } from "Search/Wiki/Flux/StoresHub";
import { TelemetryWriter } from "Search/Wiki/Flux/Sources/TelemetryWriter";
import { CountSpy } from "Search/Wiki/Flux/Sources/CountSpy";
import { TelemetrySpy } from "Search/Wiki/Flux/Sources/TelemetrySpy";
import { WikiSearchSource } from "Search/Wiki/Flux/Sources/WikiSearchSource";
import { serializeFilters, getWindowTitle } from "Search/Shared/Utils";
import { FilterKeys } from "Search/Wiki/Constants";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { PivotStateServiceSpy } from "Search/Wiki/Flux/Sources/PivotStateServiceSpy";
import { ISearchPivotStateService } from "Search/Shared/Base/PivotStateService";
import { EntityTypeUrlParam } from "Search/Wiki/Constants";
import { BasePivot } from "Search/Shared/Components/BasePivot";

export class WikiSearchPivot extends BasePivot {
    private readonly actionCreator: ActionCreator;
    private readonly storesHub: StoresHub;
    private readonly pageData: ITfsPageData | undefined;
    private readonly historyService: IVssHistoryService;
    private readonly isOrgButtonEnabled: boolean;
    private readonly countSpy: CountSpy;
    private readonly telemetrySpy: TelemetrySpy;
    private readonly telemetryWriter: TelemetryWriter;
    private readonly pivotStateServiceSpy: PivotStateServiceSpy;
    private readonly pivotStateService: ISearchPivotStateService;
    private disposeActions: (() => void)[];

    constructor(props: ITabContext, context: IVssComponentContext) {
        super(props, EntityTypeUrlParam, context);
        this.pageData = getTFSPageData(this.context.pageContext);
        const project = this.pageData && this.pageData.project;
        const filter = new Filter(
            {
                defaultState: {},
                useApplyMode: false
            });
        const actionsHub: ActionsHub = new ActionsHub();
        this.telemetryWriter = new TelemetryWriter(props.isTabSwitch(), context.pageContext, props.telemetryMarker);
        this.telemetrySpy = new TelemetrySpy(this.telemetryWriter, actionsHub, this.storesHub);

        this.storesHub = new StoresHub(actionsHub, this.context.pageContext, filter, project);
        this.actionCreator = new ActionCreator(
            this.context.pageContext,
            actionsHub,
            filter,
            {
                searchSource: new WikiSearchSource(props.isTabSwitch(), this.context.pageContext, project),
                pageSource: new PageSource(this.context.pageContext),
                orgInfoDataProviderSource: new OrgInfoDataProviderSource(this.context.pageContext)
            },
            this.storesHub.getAggregatedState,
            this.telemetryWriter);
        this.historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.isOrgButtonEnabled = isSearchOrgButtonEnabled(this.context.pageContext);
        this.countSpy = new CountSpy(this.props.resultsCount, actionsHub);
        this.pivotStateServiceSpy = new PivotStateServiceSpy(this.context.pageContext, actionsHub);
        this.pivotStateService = this.context.pageContext.getService<ISearchPivotStateService>("ISearchPivotStateService");
        this.disposeActions = [];
    }

    public render(): JSX.Element {
        return <ResultsContainer
            actionCreator={this.actionCreator}
            storesHub={this.storesHub}
            pageContext={this.context.pageContext}
            isHosted={this.props.isHosted}
            isMember={this.props.isMember}
        />;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.subscribe();
        const routeParams: { [key: string]: string } = this.historyService.getState();

        // Update search input placeholder
        this.props.inputLabel.value = {
            inputPlaceholder: Resources.SearchWikiPlaceholder,
            inputAriaLabel: this.pageData && this.pageData.project ? Resources.WikiProjectInputAriaLabel : Resources.WikiOrgInputAriaLabel
        };

        const storedFilters = this.pivotStateService.getState(EntityTypeUrlParam);
        const filtersToRestore = storedFilters && storedFilters.filtersString ? storedFilters.filtersString : this.getFilters();

        // init page.
        if (routeParams.text && routeParams.text.trim()) {
            const filters = !this.props.isTabSwitch() ? routeParams.filters : filtersToRestore;
            this.actionCreator.loadInitialState(routeParams.text, filters, routeParams.pageSize);
        } else {
            this.actionCreator.showLandingPage(this.getFilters());
        }

        // Update search provider for L1 search box
        const providerId = this.pageData && this.pageData.project
            ? "ms.vss-wiki-web.wiki-project-search-provider"
            : "ms.vss-wiki-web.wiki-collection-search-provider";
        this.context.pageContext.getService<IVssSearchService>("IVssSearchService").setSelectedProvider(providerId);
    }

    public componentWillUnmount(): void {
        this.disposeActions.forEach(action => action());
        this.actionCreator.dispose();
        this.storesHub.dispose();
        this.countSpy.dispose();
        this.props.pivotCommands.value = { farItems: [], items: [] };
        this.telemetrySpy.dispose();
        this.telemetryWriter.dispose();
        this.pivotStateServiceSpy.dispose();
        super.componentWillUnmount();
    }

    private onSearchQueryUpdated = ({ text, openInNewTab }: IInputQuery): void => {
        if (!openInNewTab && text) {
            this.actionCreator.applySearchText(text);
        } else if (text) {
            this.actionCreator.openSearchInNewTab(text);
        }
    }

    private onNotificationStoreChanged = (): void => {
        handlePivotNotification(this.props.pageNotification, this.storesHub.getAggregatedState, this.actionCreator);
    }

    private onUpdateCommands = (): void => {
        handlePivotCommands(this.props.pivotCommands, this.storesHub.getAggregatedState, this.actionCreator, this.props.isMember, this.isOrgButtonEnabled);
    }

    private subscribe = (): void => {
        const orgInfoStore = this.storesHub.getCompositeStore(["organizationInfoStore"]);
        const commandsCompositeStore = this.storesHub.getCompositeStore(["searchStore", "filterStore"]);
        const searchStore = this.storesHub.getCompositeStore(["searchStore"]);

        // Subscribe to changes in input query
        this.props.inputQuery.subscribe(this.onSearchQueryUpdated);

        orgInfoStore.addChangedListener(this.onNotificationStoreChanged);
        commandsCompositeStore.addChangedListener(this.onUpdateCommands);
        searchStore.addChangedListener(this.onSearchStoreChanged);

        this.disposeActions.push(() => orgInfoStore.removeChangedListener(this.onNotificationStoreChanged));
        this.disposeActions.push(() => commandsCompositeStore.removeChangedListener(this.onUpdateCommands));
        this.disposeActions.push(() => searchStore.removeChangedListener(this.onSearchStoreChanged));        
        this.disposeActions.push(() => this.props.inputQuery.unsubscribe(this.onSearchQueryUpdated));
    }

    private getFilters(): string | undefined {
        return this.pageData && this.pageData.project
            ? serializeFilters({
                [FilterKeys.ProjectFiltersKey]: [this.pageData.project.name]
            }) : undefined;
    }

    private onSearchStoreChanged = (): void => {
        // Update the url in case of changes in url parameters
        updateUrl(this.context.pageContext, this.storesHub.getAggregatedState);

        const { searchStoreState } = this.storesHub.getAggregatedState();
        const { query } = searchStoreState;
        if (query) {
            // update page title
            setTimeout(() => {
                const pageTitle = getWindowTitle(Resources.SearchWikiTitle, query.searchText);
                this.context
                    .pageContext
                    .getService<ITfsNavigationService>("ITfsNavigationService")
                    .setPageTitle(pageTitle);
            }, 0);
        }
    }

    protected onNavigationOccurred  = (popStateEvent: INavigationStateChangedEvent): void => {
        applyNavigatedUrl(this.actionCreator, this.historyService.getState(), this.storesHub.getAggregatedState());
    }
}

VssComponent.register("wikiSearchPivot", WikiSearchPivot);
